/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivationStart, Route, Router, Routes } from '@angular/router';

import { ConfigService } from '../config/config.service';
import { LogicService } from '../logic/logic.service';

import { PageRouteHelper } from '../../class/page-route-helper.class';
import { PageUrlHelper } from '../../class/page-url-helper.class';
import { dependencies } from '../../class/workflow.dependency';
import { ProductType, WorkflowPage } from '../../class/workflow.type';
import { WorkflowInitializeService } from '../workflow-initialize/workflow-initialize.service';
import { StorageService } from '../storage/storage.service';
import { StatefulValue } from '../../class/stateful-value.class';
import { WorkflowRouteData } from '../../class/workflow-route-data.type';
import { DynamicGuard } from '../../dynamic-guard/dynamic-guard.class';
import { StatefulService } from '@app/service/stateful.service';
import { FieldService } from '@workflow/class/field.service';
import { FlowInstance, FlowService } from '@workflow/class/flow.service';
import { PageInstance, PageService } from '@workflow/class/page.service';
import { CardService } from '@workflow/class/card.service';

/**
 * The guard interface for dynamic workflow pages in route config
 */
export interface WorkflowRouteGuard {
  canActivate?: Route['canActivate'];
  canActivateChild?: Route['canActivateChild'];
  canDeactivate?: Route['canDeactivate'];
  /**
   * @deprecated Deprecated in route, use canMatch instead.
   */
  canLoad?: Route['canLoad'];
}

export interface FilterFlowRouteArgs {
  includeFlowIds?: string[];
  excludeFlowIds?: string[];
}

/**
 * Access to pre-loaded configuration on workflows, pages, cards, and fields
 */
@Injectable({
  providedIn: 'root'
})
export class WorkflowService {
  /** an Instance to Page URL Helper  */
  private _pageUrlHelper: PageUrlHelper;

  private _routePrefix = '';

  private _currentFlowPath = '';

  private _currentPagePath = '';

  constructor(
    private readonly configService: ConfigService,
    private readonly router: Router,
    private readonly logicService: LogicService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly storageService: StorageService,
    workflowInitializeService: WorkflowInitializeService,
    private readonly statefulService: StatefulService,
    private readonly fieldService: FieldService,
    private readonly flowService: FlowService,
    private readonly cardService: CardService,
    private readonly pageService: PageService
  ) {
    workflowInitializeService.initAllDependencies();

    this._pageUrlHelper = this.flowService.createPageUrlHelper(this.activatedRoute.snapshot);

    this.router.events.subscribe((event) => {
      if (event instanceof ActivationStart) {
        const products = event.snapshot.data?.products;
        this.configService.currentProducts = products || [];
        this._pageUrlHelper = this.flowService.createPageUrlHelper(event.snapshot);
      }
    });

    this.storageService.statefulValuesUpdate.subscribe((data) => {
      StatefulValue.onBundleUpdate(data);
    });
  }

  public clearCurrentFlow(): void {
    this._currentFlowPath = '';
    this._currentPagePath = '';
  }

  /**
   * Get flow based on current path
   */
  public get currentFlow(): FlowInstance {
    return this._pageUrlHelper.getFlow(this._currentFlowPath);
  }

  /**
   * Get page based on current path
   */
  public get currentPage(): PageInstance {
    return this._pageUrlHelper.getPageFlow(this._currentPagePath);
  }

  /**
   * Find a flow by URL
   */
  public findFlowByUrl(url: string): FlowInstance {
    return this._pageUrlHelper.getFlow(url);
  }

  /**
   * Find a page in the flow by URL
   */
  public findPageByRouteUrl(routeUrl: string): PageInstance {
    const flow = this.findFlowByUrl(routeUrl);
    const flowPath = this.getFlowPathWithPrefix(flow);
    const pageWithFullPath = (page: PageInstance) => ({ page, fullPath: `/${flowPath}/${page.path}` });

    const flowPage = flow?.pages.map((page) => pageWithFullPath(page)).find((p) => p.fullPath.indexOf(routeUrl) > -1);
    return flowPage?.page;
  }

  public getFlowPathWithPrefix(flow: FlowInstance): string {
    if (flow) {
      return this._pageUrlHelper.getFlowPathWithPrefix(flow.config);
    }
  }

  /**
   * get routePrefix from widget context
   */
  public set routePrefix(routePrefix: string) {
    this._routePrefix = routePrefix ? routePrefix + '/' : '';
  }

  public get routePrefix(): string {
    return this._routePrefix;
  }

  /**
   * Set current flow path, this property is used when there is no routing defined for workflow
   */
  private set currentFlowPath(path: string) {
    this._currentFlowPath = this.routePrefix + path + '/';
  }

  /**
   * Set current page path, this property is used when there is no routing defined for workflow
   */
  private set currentPagPath(path: string) {
    this._currentPagePath = this._currentFlowPath + path;
  }

  public setCurrentFlowAndPagePath(flowPath, pagePath) {
    if (flowPath && pagePath) {
      this.currentFlowPath = flowPath;
      this.currentPagPath = pagePath;
    }
  }

  public refreshCurrentFlowAndPage() {
    // eslint-disable-next-line eqeqeq
    if (this.currentFlowPath != this.routePrefix + this.currentFlow.id) {
      this.setCurrentFlowAndPagePath(this.currentFlow.id, this.currentPage.path);
    }
  }

  public setCurrentFlowPath(flowPath) {
    if (flowPath) {
      this.currentFlowPath = flowPath;
    }
  }

  public getFlows(): FlowInstance[] {
    const flowsIds = Object.keys(dependencies.config.flows);
    if (!flowsIds) {
      return [];
    }

    return flowsIds.map((flowId) => this.flowService.createFlow(flowId));
  }

  public getFirstFlow(): FlowInstance {
    return this.getFlows()[0];
  }

  public getFlowById(flowId: string): FlowInstance {
    return this.getFlows().find((f) => f.id === flowId);
  }

  public findPage(pageId: string, pageIterationId?: number): PageInstance {
    const page = this.getPageByIdInCurrentFlow(pageId, pageIterationId);
    if (page) {
      this.currentPagPath = page.path;
      return page;
    }

    throw new Error(`page with id ${pageId} was not found in current flow ${this.currentFlow.id}`);
  }

  public findNextReviewPage(currentPage?: PageInstance) {
    const current = currentPage || this.currentPage;
    const currrentIndex = this.currentFlow.pages.findIndex((t) => t.id === current.id);
    const legacyPage = this.currentFlow.pages.find((t, i) => t.config.customPageType === 'legacy-review' && i > currrentIndex);

    if (legacyPage?.visible) {
      return legacyPage;
    }
  }

  /**
   * Find the first incomplete page in the flow
   * @returns Page
   */
  public findFirstIncompletePage() {
    const flowId = this.currentFlow.id.includes('oao') ? 'api' : 'ola';
    const extraData = this.statefulService.wrap(`${flowId}:extraData`).valueString;
    let extraDataObj;
    try {
      extraDataObj = JSON.parse(extraData);
    } catch (error) {
      extraDataObj = undefined;
    }

    // Get incomplete pages
    const incompletedPages = this.currentFlow.pages.filter(
      (t) => (t.visible && !t.helper.isPageComplete && !t.config?.actionLogic) || !this.isExtradataCompleted(t.id, extraDataObj)
    );
    if (incompletedPages && incompletedPages.length > 0 && incompletedPages[0]) {
      return incompletedPages[0];
    }
    return null;
  }

  /**
   * Check this specific page is completed or not
   * @param id pageid string
   * @param extraDataObj extraData Object
   * @returns boolean
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  private isExtradataCompleted(id: string, extraDataObj: Object): boolean {
    if (!extraDataObj) {
      return false;
    }

    return !((!extraDataObj[id]?.isVisited || extraDataObj[id]?.state?.toLowerCase() !== 'completed') && extraDataObj[id] !== undefined);
  }

  public findNextPage(currentPage?: PageInstance): PageInstance {
    const current = currentPage || this.currentPage;
    const next = current?.nextPage;
    if (next) {
      this.currentPagPath = next ? next.path : '';
      return next;
    }
  }

  /** Method used to check whether a page is hidden in admin or not */
  public isHiddenInAdmin(pageId: string, flowId: string): boolean {
    const instanceConfigId = `${flowId}/${pageId}`;
    let page: WorkflowPage;
    if (dependencies.config.workflowSpecificPages[instanceConfigId]) {
      page = { ...dependencies.config.pages[pageId], ...dependencies.config.workflowSpecificPages[instanceConfigId] };
    } else {
      page = dependencies.config.pages[pageId];
    }

    return !!dependencies.logicService.hasFunction('never', page.visible);
  }

  public findPreviousPage(currentPage?: PageInstance): PageInstance {
    const current = currentPage || this.currentPage;
    const previous = current?.previousPage;
    if (previous) {
      this.currentPagPath = previous ? previous.path : '';
      return previous;
    }
  }

  /**
   * NOTE: this should be turned private and called from the constructor (instead of the app module), once the
   * PageComponent is available in this module
   * @param pageComponentClass reference to class PageComponent
   */
  public addRoutes(
    pageComponentClass: Route['component'],
    guards?: WorkflowRouteGuard,
    routeFilter?: FilterFlowRouteArgs,
    ...products: ProductType[]
  ): void {
    this.addRoutesWithPrefix('', pageComponentClass, guards, routeFilter, ...products);
  }

  public addRoutesWithPrefix(
    routePrefix: string,
    pageComponentClass: Route['component'],
    guards?: WorkflowRouteGuard,
    routeFilter?: FilterFlowRouteArgs,
    ...products: ProductType[]
  ): void {
    const workflowRoutes = this.buildNgRoutes(routePrefix, pageComponentClass, guards, routeFilter, ...products);
    this.addToRouter(workflowRoutes);
  }

  public buildNgRoutes(
    routePrefix: string,
    pageComponentClass: Route['component'],
    guards?: WorkflowRouteGuard,
    routeFilter?: FilterFlowRouteArgs,
    ...products: ProductType[]
  ): Routes {
    const workflowRoutes: Routes = [];

    const excludeFlowIds = routeFilter?.excludeFlowIds || [];
    const includeFlowIds = routeFilter?.includeFlowIds || [];
    const config = products?.length ? this.configService.configByProducts(products) : this.configService.config;
    const workflows = config.workflows;
    if (!workflows) {
      return;
    }

    for (const workflowId of Object.keys(workflows)) {
      const workflow = workflows[workflowId];
      if (!workflow.pages) {
        continue;
      }

      if (includeFlowIds.length && !includeFlowIds.includes(workflowId)) {
        continue;
      }

      if (excludeFlowIds.length && excludeFlowIds.includes(workflowId)) {
        continue;
      }

      const flowPath = workflow.path;
      for (const pageId of workflow.pages) {
        const page = config['workflow-pages'][pageId];
        // We don't want to add Route for Redirect or Action pages
        if (!this.pageService.isPageType(page.type)) {
          continue;
        }

        const pageRouteHelper = new PageRouteHelper(page);
        if (page) {
          const prefix = routePrefix ? `${routePrefix}/` : '';
          workflow.routePrefix = prefix;
          const path = `${flowPath}/${pageRouteHelper.PageRoutePath}`;
          if (this.pathExistInRouterConfig(path)) {
            break;
          } else {
            const data = {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              pageData: page.routeData ? this.logicService.evaluate<any>(page.routeData, { route: { workflow, page } }, 'generic') : [],
              pageId,
              products,
              workflowId
            } as WorkflowRouteData;

            const finalGuards = new DynamicGuard(workflow, page).buildRouteGuard(guards);
            workflowRoutes.unshift({ component: pageComponentClass, path, ...finalGuards, data });
          }
        }
      }
    }

    return workflowRoutes;
  }

  private addToRouter(routes: Routes = []) {
    this.router.config.unshift(...routes);
    this.router.resetConfig(this.router.config);
  }

  private pathExistInRouterConfig(path: string): boolean {
    for (const route of this.router.config) {
      if (route.path === path) {
        return true;
      }
    }

    return false;
  }

  private getPageByIdInCurrentFlow(pageId: string, pageIterationId?: number): PageInstance {
    return this.currentFlow.pages.find((p) => p.id === pageId && (p.pageIterationId === pageIterationId || !pageIterationId));
  }

  /** Clear all instances of pages,card,field and statefulValue */
  public clearAllInstance(): void {
    // TODO: Since the statefulService serves up stateful values, this line probably still works. Should we add a clear
    // method to the stateful service?
    StatefulValue.clear();
    this.fieldService.clear();
    // TODO: Adding this as we transition to services. This should accomplish the same as above, but for service generated fields.
    // Will add same for card and page when we use them.
    this.fieldService.clear();
    this.cardService.clear();
    this.pageService.clear();
  }
}
